import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { JwtClaims, LoginResponse, RefreshRequest } from "@dto/login";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { useModelStore, useUserStore } from "src/store";
import { BASE_URL, USER_ORG_ID, USERNAME } from "./constants";


export const preventDefault = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
}

export function clearAuth() {
    const updateUsername = useUserStore.getState().updateUsername;
    const updateOrgId = useUserStore.getState().updateOrgId;
    const updateRole = useModelStore.getState().updateUserRole;

    // Supprimer les cookies http-only et secure via le serveur
    // Le serveur doit répondre avec Set-Cookie pour supprimer les cookies
    post(BASE_URL + '/auth/logout', {}, 0).catch(() => {
        // Ignorer les erreurs, on continue quand même la déconnexion
    });
    
    // Si access_token/refresh_token sont des cookies normaux (non http-only),
    // ces lignes les suppriment. Si ils sont http-only, ces lignes ne font rien
    // mais ne causent pas d'erreur, donc on les garde pour compatibilité
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    
    updateUsername('');
    updateOrgId('');
    updateRole(null);
    window.location.href = '/login';
}

export const MAX_RETRY = 3;

// i18n codes for the statuses that come back without a CompozzError envelope,
// typically emitted by nginx itself (413 on an oversized body, 502/503/504
// pages) before the request ever reaches a Compozz service.
const I18N_BY_STATUS: Record<number, string> = {
    408: "general.server.timeout.0",
    413: "general.request.too.large.0",
    502: "general.server.disconnected.0",
    503: "general.server.disconnected.0",
    504: "general.server.timeout.0",
};
const HTML_BODY = /^\s*</;
const MAX_ERROR_MESSAGE_LENGTH = 300;

// Returns the i18n code and params describing an HTTP status that carries no
// CompozzError envelope.
export function i18nForStatus(status: number): [string, any[]] {
    const code = I18N_BY_STATUS[status];
    return code ? [code, []] : ["general.server.error.1", [status]];
}

function firstString(...values: any[]): string {
    for (const value of values) {
        if (typeof value === "string" && value !== "") {
            return value;
        }
    }
    return "";
}

// Builds a CompozzError from a non-2xx response. The body is the JSON
// CompozzError envelope in the nominal case, but it can also be an HTML page
// (nginx 413/504), plain text (WriteError fallback in compozz-common) or an
// unrelated JSON object: none of these must ever reject the promise.
export async function errorFromResponse(response: Response): Promise<CompozzError> {
    let text = "";
    try {
        text = await response.text();
    } catch (err) {
        console.error(err);
    }
    let parsed: any = null;
    if (text) {
        try {
            parsed = JSON.parse(text);
        } catch {
            parsed = null;
        }
    }
    if (isCompozzError(parsed)) {
        if (!parsed.code) {
            parsed.code = response.status;
        }
        return parsed;
    }
    const [i18n, params] = i18nForStatus(response.status);
    const error = new CompozzError(response.status, i18n, params);
    const bodyMessage = (parsed && typeof parsed === "object")
        ? firstString(parsed.error_description, parsed.error, parsed.message)
        : (HTML_BODY.test(text) ? "" : text.trim().slice(0, MAX_ERROR_MESSAGE_LENGTH));
    error.message = bodyMessage || response.statusText || `HTTP ${response.status}`;
    return error;
}

// Converts a rejected fetch chain (network failure, connection reset by the
// proxy on an oversized body, JSON parse failure on a 2xx body) into a
// CompozzError, so callers never face a rejected promise. An aborted request
// keeps resolving null: callers treat it as "nothing to do".
export function errorFromException(err: any): CompozzError | null {
    if (err?.name === "AbortError") {
        return null;
    }
    console.error(err);
    const error = new CompozzError(0, "general.server.disconnected.0", []);
    error.message = firstString(err?.message) || String(err);
    return error;
}

export function refreshToken(): Promise<boolean> {
    const updateUsername = useUserStore.getState().updateUsername;
    const updateOrgId = useUserStore.getState().updateOrgId;

    const orgId = useUserStore.getState().orgId || localStorage.getItem(USER_ORG_ID);
    const username = useUserStore.getState().username || localStorage.getItem(USERNAME);
    if (!orgId || !username) {
        clearAuth();
        return Promise.resolve(false);
    }

    const refreshRequest: RefreshRequest = {
        refresh_token: undefined, // not needed anymore
        orgId: orgId
    };

    const result = fetch(BASE_URL + '/auth/refresh', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Origin': 'https://localhost',
        },
        credentials: 'include',
        body: JSON.stringify(refreshRequest)
    }).then(response => {
        if (!response.ok) {
            clearAuth();
            return Promise.resolve(null);
        } else {
            return response.json() as Promise<LoginResponse>;
        }
    }).catch(error => {
        console.error('Error checking login status:', error);
        clearAuth();
        return Promise.resolve(null);
    }).then(data => {
        if (data) {
            const decoded = jwtDecode<JwtClaims>(data.access_token);
            const orgId = Object.keys(decoded.organization)[0];
            updateUsername(decoded.preferred_username);
            updateOrgId(orgId);
            return true;
        }
        return false;
    });

    return result;
}

export function get<T>(url: string, retryNumber: number = 0): Promise<T | null | CompozzError> {
    // let accessToken = Cookies.get('access_token');
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    // if (accessToken) {
    //     requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    // }
    return fetch(url, {
        headers: requestHeaders,
        credentials: 'include'
    })
        .then(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                if (response.status === 401 || response.status === 403) {
                    const result = refreshToken().then(success => {
                        if (success) {
                            if (retryNumber < MAX_RETRY) {
                                return get<T>(url, retryNumber + 1);
                            } else {
                                return errorFromResponse(response);
                            }
                        } else {
                            clearAuth();
                            return errorFromResponse(response);
                        }
                    }).catch(err => {
                        clearAuth();
                        return errorFromException(err);
                    });
                    return result;
                }
                return errorFromResponse(response);
            }
            return response.text().then(text => {
                return JSON.parse(text, reviver) as Promise<T>
            })
        })
        .catch(errorFromException)
}

export function post<T>(url: string, body: any, retryNumber: number = 0, headers: Headers | undefined = undefined, signal?: AbortSignal): Promise<T | null | CompozzError> {
    // let accessToken = Cookies.get('access_token');
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    if (headers) {
        headers.forEach((value, key) => {
            requestHeaders.set(key, value);
        });
    }
    // if (accessToken) {
    //     requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    // }
    return fetch(url, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(body, replacer),
        credentials: 'include',
        signal: signal
    })
        .then(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                if (response.status === 401 || response.status === 403) {
                    const result = refreshToken().then(success => {
                        if (success) {
                            if (retryNumber < MAX_RETRY) {
                                return post<T>(url, body, retryNumber + 1, undefined, signal);
                            } else {
                                return errorFromResponse(response);
                            }
                        } else {
                            clearAuth();
                            return errorFromResponse(response);
                        }
                    }).catch(err => {
                        clearAuth();
                        return errorFromException(err);
                    });
                    return result;
                } else {
                    return errorFromResponse(response);
                }
            }
            if (response.status === 204) {
                response.text();
                return null;
            }
            return response.text().then(text => {
                return JSON.parse(text, reviver) as Promise<T>
            })
        })
        .catch(errorFromException)
}

export function simplePost<T>(url: string, body: any, retryNumber: number = 0): Promise<T | null | CompozzError> {
    // let accessToken = Cookies.get('access_token');
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    // if (accessToken) {
    //     requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    // }
    return fetch(url, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(body, replacer),
        credentials: 'include'
    })
        // Explicit result type: without the 401/403 retry branch of the other
        // verbs, inference cannot unify the CompozzError / T / null returns.
        .then<T | null | CompozzError>(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                return errorFromResponse(response);
            }
            if (response.status === 204) {
                response.text();
                return null;
            }
            return response.text().then(text => {
                return JSON.parse(text, reviver) as Promise<T>
            })
        })
        .catch(errorFromException)
}

export function put<T>(url: string, body: any, retryNumber: number = 0): Promise<T | null | CompozzError> {
    // let accessToken = Cookies.get('access_token');
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    // if (accessToken) {
    //     requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    // }
    return fetch(url, {
        method: 'PUT',
        headers: requestHeaders,
        body: JSON.stringify(body, replacer),
        credentials: 'include'
    })
        .then(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                if (response.status === 401 || response.status === 403) {
                    const result = refreshToken().then(success => {
                        if (success) {
                            if (retryNumber < MAX_RETRY) {
                                return put<T>(url, body, retryNumber + 1);
                            } else {
                                return errorFromResponse(response);
                            }
                        } else {
                            clearAuth();
                            return errorFromResponse(response);
                        }
                    }).catch(err => {
                        clearAuth();
                        return errorFromException(err);
                    });
                    return result;
                }
                return errorFromResponse(response);
            }
            if (response.status === 204) {
                response.text();
                return null;
            }
            return response.text().then(text => {
                return JSON.parse(text, reviver) as Promise<T>
            })
        })
        .catch(errorFromException)
}

export function patch<T>(url: string, body: any, retryNumber: number = 0): Promise<T | null | CompozzError> {
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    return fetch(url, {
        method: 'PATCH',
        headers: requestHeaders,
        body: JSON.stringify(body, replacer),
        credentials: 'include'
    })
        .then(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                if (response.status === 401 || response.status === 403) {
                    const result = refreshToken().then(success => {
                        if (success) {
                            if (retryNumber < MAX_RETRY) {
                                return patch<T>(url, body, retryNumber + 1);
                            } else {
                                return errorFromResponse(response);
                            }
                        } else {
                            clearAuth();
                            return errorFromResponse(response);
                        }
                    }).catch(err => {
                        clearAuth();
                        return errorFromException(err);
                    });
                    return result;
                }
                return errorFromResponse(response);
            }
            if (response.status === 204) {
                response.text();
                return null;
            }
            return response.text().then(text => {
                return JSON.parse(text, reviver) as Promise<T>
            })
        })
        .catch(errorFromException)
}

export function del<T>(url: string, body: any, retryNumber: number = 0): Promise<T | null | CompozzError> {
    // let accessToken = Cookies.get('access_token');
    const requestHeaders = new Headers();
    requestHeaders.set('Accept', 'application/json');
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Origin', 'https://localhost');
    // if (accessToken) {
    //     requestHeaders.set('Authorization', `Bearer ${accessToken}`);
    // }
    return fetch(url, {
        method: 'DELETE',
        headers: requestHeaders,
        body: JSON.stringify(body, replacer),
        credentials: 'include'
    })
        .then(response => {
            if (!response.ok) {
                if (response.status === 502) {
                    return Promise.resolve(new CompozzError(502, "general.server.disconnected.0", []));
                }
                if (response.status === 402) {
                    return Promise.resolve(new CompozzError(402, "general.free.trial.expired.0", []));
                }
                if (response.status === 401 || response.status === 403) {
                    const result = refreshToken().then(success => {
                        if (success) {
                            if (retryNumber < MAX_RETRY) {
                                return del<T>(url, body, retryNumber + 1);
                            } else {
                                return errorFromResponse(response);
                            }
                        } else {
                            clearAuth();
                            return errorFromResponse(response);
                        }
                    }).catch(err => {
                        clearAuth();
                        return errorFromException(err);
                    });
                    return result;
                }
                return errorFromResponse(response);
            }
            if (response.status === 204) {
                response.text();
                return null;
            }
            return response.json()
        })
        .catch(errorFromException)
}

export function replacer(key: string, value: any) {
    // Transient fields
    if (key === 'parsedDate') return undefined;

    if (value instanceof Map) {
        const map = Object.create(null);
        value.forEach((v, k) => {
            map[k] = v;
        });

        return map;
    } else {
        return value;
    }
}

const isObject = (value: any) => {
    return typeof value === 'object'
        && value !== null
        && !Array.isArray(value)
        && !(value instanceof RegExp)
        && !(value instanceof Date)
        && !(value instanceof Set)
        && !(value instanceof Map)
}

export function reviver(key: string, value: any) {

    if (value != null && (key === 'format' || key === 'typeItems' || key === 'fields' || key === 'aggregations')) {
        if (isObject(value)) {
            return toMap<any>(value);
        } else {
            return value;
        }
    } else if (value != null && key === 'formats') {
        if (isObject(value)) {
            return toMap<Map<string, any>>(value, (v) => toMap<any>(v));
        } else {
            return value;
        }
    } else if (value != null && key === 'period' && isObject(value)) {
        const { Period } = require('@dto/period');
        return Period.fromObject(value) || value;
    }
    return value;
}

export function toMap<T>(object: any, applyOnValues: (value: any) => any = (value: any) => value): Map<string, T> | null {
    if (isObject(object)) {
        const res = new Map<string, T>();
        Object.keys(object).forEach((k) => {
            res.set(k, applyOnValues(object[k]));
        });
        return res;
    } else {
        return object;
    }
}

// Canvas context for text measurement (reused for performance)
let textMeasurementCanvas: HTMLCanvasElement | null = null;

/**
 * Measures the width of text in pixels based on the given font properties
 * @param text The text to measure
 * @param fontSize Font size (e.g., "14px", "1rem")
 * @param fontFamily Font family (e.g., "Arial", "sans-serif")
 * @param fontWeight Font weight (e.g., "normal", "bold", "600")
 * @returns Width in pixels
 */
export function measureTextWidth(
    text: string,
    fontSize: string = '14px',
    fontFamily: string = 'sans-serif',
    fontWeight: string = 'normal'
): number {
    if (!textMeasurementCanvas) {
        textMeasurementCanvas = document.createElement('canvas');
    }
    
    const context = textMeasurementCanvas.getContext('2d');
    if (!context) return 0;
    
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`;
    const metrics = context.measureText(text);
    
    return Math.ceil(metrics.width);
}

/**
 * Computes the width needed for text content with optional padding
 * @param text The text to measure
 * @param fontSize Font size (default: "14px")
 * @param fontFamily Font family (default: "sans-serif")
 * @param fontWeight Font weight (default: "normal")
 * @param paddingX Horizontal padding in pixels (default: 0)
 * @returns Width in pixels
 */
export function computeTextWidth(
    text: string,
    fontSize?: string,
    fontFamily?: string,
    fontWeight?: string,
    paddingX: number = 0
): number {
    const textWidth = measureTextWidth(text, fontSize, fontFamily, fontWeight);
    return textWidth + paddingX;
}
